/**
 * Leviathan - Sales Dashboard
 * Quasar Prime LLC
 *
 * Overview of sales pipeline, leads, and activities
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Target,
  Users,
  TrendingUp,
  Calendar,
  Phone,
  Mail,
  CheckCircle,
  Clock,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { API_URL } from '@/config/api';

// Lead status colors
const STATUS_COLORS: Record<string, string> = {
  NEW: 'bg-blue-500',
  CONTACTED: 'bg-cyan-500',
  QUALIFIED: 'bg-indigo-500',
  PROPOSAL: 'bg-purple-500',
  NEGOTIATION: 'bg-amber-500',
  WON: 'bg-emerald-500',
  LOST: 'bg-red-500',
  DORMANT: 'bg-slate-400',
};

const STATUS_LABELS: Record<string, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUALIFIED: 'Qualified',
  PROPOSAL: 'Proposal',
  NEGOTIATION: 'Negotiation',
  WON: 'Won',
  LOST: 'Lost',
  DORMANT: 'Dormant',
};

// Activity type icons
const ACTIVITY_ICONS: Record<string, typeof Phone> = {
  CALL: Phone,
  EMAIL: Mail,
  MEETING: Users,
  TASK: CheckCircle,
  NOTE: Target,
  SITE_VISIT: Target,
  QUOTE_SENT: Target,
  FOLLOW_UP: Clock,
};

interface PipelineItem {
  status: string;
  count: number;
  value: number;
}

interface LeadStats {
  period: { days: number; startDate: string };
  totalLeads: number;
  newLeads: number;
  wonLeads: number;
  lostLeads: number;
  avgScore: number;
  conversionRate: number;
  bySource: Array<{ source: string; count: number }>;
}

interface ActivityStats {
  period: { days: number; startDate: string };
  totalActivities: number;
  completedCount: number;
  pendingCount: number;
  overdueCount: number;
  completionRate: number;
  byType: Array<{ type: string; count: number }>;
}

interface Lead {
  id: string;
  leadNumber: string;
  companyName: string;
  contactFirstName: string | null;
  contactLastName: string | null;
  status: string;
  score: number;
  nextFollowUpDate: string | null;
  assignedTo?: {
    firstName: string | null;
    lastName: string | null;
  };
}

interface Activity {
  id: string;
  type: string;
  subject: string;
  status: string;
  scheduledDate: string | null;
  lead?: {
    id: string;
    leadNumber: string;
    companyName: string;
  };
  customer?: {
    id: string;
    customerNumber: string;
    name: string;
  };
}

export default function SalesDashboard() {
  const { accessToken } = useAuth();
  const [statsDays] = useState(30);

  // Fetch pipeline summary
  const { data: pipelineData } = useQuery({
    queryKey: ['lead-pipeline'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/leads/pipeline`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch pipeline');
      return res.json();
    },
  });

  // Fetch lead statistics
  const { data: leadStats } = useQuery<LeadStats>({
    queryKey: ['lead-stats', statsDays],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/leads/stats?days=${statsDays}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch lead stats');
      return res.json();
    },
  });

  // Fetch activity statistics
  const { data: activityStats } = useQuery<ActivityStats>({
    queryKey: ['activity-stats', statsDays],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/activities/stats?days=${statsDays}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch activity stats');
      return res.json();
    },
  });

  // Fetch follow-ups due
  const { data: followUps } = useQuery<Lead[]>({
    queryKey: ['lead-followups'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/leads/follow-ups`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch follow-ups');
      return res.json();
    },
  });

  // Fetch overdue activities
  const { data: overdueActivities } = useQuery<Activity[]>({
    queryKey: ['activities-overdue'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/activities/overdue`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch overdue activities');
      return res.json();
    },
  });

  // Fetch scheduled activities
  const { data: scheduledActivities } = useQuery<Activity[]>({
    queryKey: ['activities-scheduled'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/activities/scheduled?days=7`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch scheduled activities');
      return res.json();
    },
  });

  const pipeline: PipelineItem[] = pipelineData?.pipeline || [];
  const totalPipelineValue = pipelineData?.totalValue || 0;

  // Calculate active pipeline (exclude WON, LOST, DORMANT)
  const activePipeline = pipeline.filter(
    (p) => !['WON', 'LOST', 'DORMANT'].includes(p.status)
  );
  const activePipelineValue = activePipeline.reduce((sum, p) => sum + p.value, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Sales Dashboard</h1>
          <p className="text-slate-400">Pipeline overview and activity tracking</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/sales/leads/new"
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
          >
            + New Lead
          </Link>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Pipeline Value */}
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Active Pipeline</p>
              <p className="text-2xl font-bold text-slate-100 mt-1">
                ${activePipelineValue.toLocaleString()}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {activePipeline.reduce((sum, p) => sum + p.count, 0)} active leads
              </p>
            </div>
            <div className="p-3 bg-amber-500/10 rounded-lg">
              <TrendingUp className="w-6 h-6 text-amber-500" />
            </div>
          </div>
        </div>

        {/* Conversion Rate */}
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Conversion Rate</p>
              <p className="text-2xl font-bold text-emerald-400 mt-1">
                {leadStats?.conversionRate || 0}%
              </p>
              <p className="text-sm text-slate-500 mt-1">Last {statsDays} days</p>
            </div>
            <div className="p-3 bg-emerald-500/10 rounded-lg">
              <Target className="w-6 h-6 text-emerald-500" />
            </div>
          </div>
        </div>

        {/* New Leads */}
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">New Leads</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">
                {leadStats?.newLeads || 0}
              </p>
              <p className="text-sm text-slate-500 mt-1">Last {statsDays} days</p>
            </div>
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
          </div>
        </div>

        {/* Activities Completed */}
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Activities Done</p>
              <p className="text-2xl font-bold text-purple-400 mt-1">
                {activityStats?.completedCount || 0}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {activityStats?.completionRate || 0}% completion
              </p>
            </div>
            <div className="p-3 bg-purple-500/10 rounded-lg">
              <CheckCircle className="w-6 h-6 text-purple-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline Visualization */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100">Sales Pipeline</h2>
          <Link
            to="/sales/leads"
            className="text-amber-500 hover:text-amber-400 text-sm flex items-center gap-1"
          >
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Pipeline Stages */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'].map(
            (status) => {
              const stageData = pipeline.find((p) => p.status === status) || {
                count: 0,
                value: 0,
              };
              const percentage =
                totalPipelineValue > 0
                  ? (stageData.value / totalPipelineValue) * 100
                  : 0;

              return (
                <Link
                  key={status}
                  to={`/sales/leads?status=${status}`}
                  className="flex-1 min-w-[140px] bg-slate-900/50 rounded-lg p-4 hover:bg-slate-900 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${STATUS_COLORS[status]}`} />
                    <span className="text-slate-300 text-sm font-medium">
                      {STATUS_LABELS[status]}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-slate-100">{stageData.count}</p>
                  <p className="text-sm text-slate-500">
                    ${stageData.value.toLocaleString()}
                  </p>
                  {/* Value bar */}
                  <div className="mt-2 h-1 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${STATUS_COLORS[status]}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </Link>
              );
            }
          )}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Follow-ups Due */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-100">Follow-ups Due</h2>
            <span className="px-2 py-1 bg-amber-500/10 text-amber-500 rounded-full text-xs font-medium">
              {followUps?.length || 0} pending
            </span>
          </div>

          {followUps && followUps.length > 0 ? (
            <div className="space-y-3">
              {followUps.slice(0, 5).map((lead) => (
                <Link
                  key={lead.id}
                  to={`/sales/leads/${lead.id}`}
                  className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-slate-200">{lead.companyName}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500">{lead.leadNumber}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[lead.status]} text-white`}
                      >
                        {STATUS_LABELS[lead.status]}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-amber-500 font-medium">Score: {lead.score}</p>
                    {lead.nextFollowUpDate && (
                      <p className="text-xs text-slate-500">
                        {new Date(lead.nextFollowUpDate).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No follow-ups due</p>
            </div>
          )}

          {followUps && followUps.length > 5 && (
            <Link
              to="/sales/leads?hasFollowUp=true"
              className="block text-center mt-4 text-amber-500 hover:text-amber-400 text-sm"
            >
              View all {followUps.length} follow-ups
            </Link>
          )}
        </div>

        {/* Overdue Activities */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-100">Overdue Activities</h2>
            {overdueActivities && overdueActivities.length > 0 && (
              <span className="px-2 py-1 bg-red-500/10 text-red-500 rounded-full text-xs font-medium">
                {overdueActivities.length} overdue
              </span>
            )}
          </div>

          {overdueActivities && overdueActivities.length > 0 ? (
            <div className="space-y-3">
              {overdueActivities.slice(0, 5).map((activity) => {
                const IconComponent = ACTIVITY_ICONS[activity.type] || Clock;
                return (
                  <div
                    key={activity.id}
                    className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-red-500/20"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-500/10 rounded-lg">
                        <IconComponent className="w-4 h-4 text-red-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-200">{activity.subject}</p>
                        <p className="text-xs text-slate-500">
                          {activity.lead?.companyName || activity.customer?.name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-red-400 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      {activity.scheduledDate &&
                        new Date(activity.scheduledDate).toLocaleDateString()}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No overdue activities</p>
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Activities */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100">Upcoming Activities</h2>
          <Link
            to="/sales/activities"
            className="text-amber-500 hover:text-amber-400 text-sm flex items-center gap-1"
          >
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {scheduledActivities && scheduledActivities.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {scheduledActivities.slice(0, 6).map((activity) => {
              const IconComponent = ACTIVITY_ICONS[activity.type] || Clock;
              return (
                <div
                  key={activity.id}
                  className="p-4 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-500/10 rounded-lg">
                      <IconComponent className="w-4 h-4 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-200 truncate">
                        {activity.subject}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {activity.lead?.companyName || activity.customer?.name}
                      </p>
                      <p className="text-xs text-amber-500 mt-1">
                        {activity.scheduledDate &&
                          new Date(activity.scheduledDate).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No upcoming activities scheduled</p>
          </div>
        )}
      </div>

      {/* Activity Stats by Type */}
      {activityStats && activityStats.byType.length > 0 && (
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-100">Activity Breakdown</h2>
            <span className="text-sm text-slate-500">Last {statsDays} days</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {activityStats.byType.map((item) => {
              const IconComponent = ACTIVITY_ICONS[item.type] || Clock;
              return (
                <div
                  key={item.type}
                  className="text-center p-3 bg-slate-900/50 rounded-lg"
                >
                  <div className="p-2 bg-slate-700/50 rounded-lg w-fit mx-auto mb-2">
                    <IconComponent className="w-5 h-5 text-amber-500" />
                  </div>
                  <p className="text-xl font-bold text-slate-100">{item.count}</p>
                  <p className="text-xs text-slate-500">{item.type.replace('_', ' ')}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
